import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import Snackbar from "@material-ui/core/Snackbar";
import MuiAlert from "@material-ui/lab/Alert";
import Autocomplete from "@material-ui/lab/Autocomplete";
import ActionModal from "../Common/ActionModal";
import { getJwtToken } from "../../api";

const useStyles = makeStyles((theme) => ({
  field: {
    marginTop: theme.spacing(3),
  },
  helperText: {
    marginTop: theme.spacing(3),
    color: "#666",
  },
}));

const EMAIL_TYPE_OPTIONS = [
  {
    label: "営巣報告メール（ツルツタ１をテスト用として使用）",
    value: "nesting_report_tsurutsuta1_test",
  },
];

const SUBJECT_PLACEHOLDER = "サーバー側で動的に設定されます";

const normalizeApiBaseUrl = (baseUrl) => (baseUrl || "").replace(/\/$/, "");

const parseRecipients = (rawRecipients) =>
  rawRecipients
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const EmailTestModal = ({ open, onClose }) => {
  const classes = useStyles();
  const initialRecipient = useMemo(() => {
    try {
      return localStorage.getItem("email") || "";
    } catch (e) {
      return "";
    }
  }, []);

  const [recipientsInput, setRecipientsInput] = useState(initialRecipient);
  const [selectedType, setSelectedType] = useState(EMAIL_TYPE_OPTIONS[0]);
  const [submitting, setSubmitting] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState("success");

  const recipients = parseRecipients(recipientsInput);
  const hasInvalidEmail = recipients.some((mail) => !isValidEmail(mail));
  const canSubmit = recipients.length > 0 && !hasInvalidEmail && selectedType;

  const handleClose = () => onClose();
  const handleToastClose = (event, reason) => {
    if (reason === "clickaway") return;
    setToastOpen(false);
  };

  const openToast = (message, severity) => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  const getAuthToken = async () => {
    const storedToken = localStorage.getItem("idToken");
    if (storedToken) {
      return storedToken;
    }
    return getJwtToken();
  };

  const handleSubmit = async () => {
    if (!canSubmit || submitting) {
      return;
    }

    setSubmitting(true);

    const apiBaseUrl = normalizeApiBaseUrl(process.env.REACT_APP_API_BASE_URL);
    if (!apiBaseUrl) {
      openToast("API接続先が未設定です。環境変数を確認してください。", "error");
      setSubmitting(false);
      handleClose();
      return;
    }
    const endpoint = `${apiBaseUrl}/email`;

    try {
      const requestedBy = localStorage.getItem("email") || "";
      let token = await getAuthToken();
      const body = {
        recipients,
        emailType: selectedType.value,
        subject: null,
        requestedBy,
      };

      let response = await fetch(endpoint, {
        method: "POST",
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      if (response.status === 401) {
        token = await getJwtToken();
        response = await fetch(endpoint, {
          method: "POST",
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        });
      }

      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`);
      }

      openToast("メール送信を受け付けました。", "success");
      handleClose();
    } catch (e) {
      console.error("Failed to invoke email lambda:", e);
      openToast("メール送信に失敗しました。設定またはAPIを確認してください。", "error");
      handleClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <ActionModal
        open={open}
        onClose={handleClose}
        onSubmit={handleSubmit}
        submitLabel="送信"
        cancelLabel="キャンセル"
        submitDisabled={!canSubmit}
        submitting={submitting}
        title="メール送信テスト"
        maxWidth="sm"
      >
        {/* <Box className={classes.field}>
          <TextField
            label="件名"
            fullWidth
            variant="outlined"
            size="small"
            value={SUBJECT_PLACEHOLDER}
            disabled
          />
        </Box> */}

        <Box className={classes.field}>
          <TextField
            label="メール送信先（カンマ区切りで複数指定可）"
            fullWidth
            variant="outlined"
            size="small"
            value={recipientsInput}
            onChange={(event) => setRecipientsInput(event.target.value)}
            error={hasInvalidEmail}
            helperText={hasInvalidEmail ? "メールアドレス形式を確認してください。" : ""}
          />
        </Box>

        <Box className={classes.field}>
          <Autocomplete
            options={EMAIL_TYPE_OPTIONS}
            getOptionLabel={(option) => option.label}
            getOptionSelected={(option, value) => option.value === value.value}
            value={selectedType}
            onChange={(event, newValue) => setSelectedType(newValue)}
            disableClearable
            renderInput={(params) => (
              <TextField {...params} label="メール種類" variant="outlined" size="small" />
            )}
          />
        </Box>

        <Typography variant="body2" className={classes.helperText}>
          送信ボタン押下で メール送信を実行します。
        </Typography>
      </ActionModal>

      <Snackbar
        open={toastOpen}
        autoHideDuration={5000}
        onClose={handleToastClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert onClose={handleToastClose} severity={toastSeverity} variant="filled" elevation={6}>
          {toastMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
};

EmailTestModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default EmailTestModal;
